import React, {useState, useEffect} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import axios from 'axios';
import {format, parseISO} from 'date-fns';
import {useAuth} from '../contexts/AuthContext';

const {FiUserCheck, FiDollarSign, FiCalendar, FiTrendingUp, FiSearch, FiX, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight, FiEye} = FiIcons;

const Doctors = () => {
  const [doctors, setDoctors] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [allPayouts, setAllPayouts] = useState([]);
  const [filteredPayouts, setFilteredPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [dateRange, setDateRange] = useState({
    start_date: format(new Date(new Date().setDate(1)), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd')
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  
  // View Details Modal state
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 20,
    totalItems: 0
  });

  const {user} = useAuth();

  // Format Patient ID with leading zeroes
  const formatPatientId = (id) => {
    return String(id).padStart(6, '0');
  };

  useEffect(() => {
    fetchDoctors();
    // FIXED: If user is a doctor, automatically filter to show only their payouts using doctor record ID
    if (user.role === 'doctor') {
      // We'll set the selectedDoctor after we get the doctor record ID
      fetchDoctorRecord();
    } else {
      fetchPayouts();
    }
  }, [dateRange]);

  // FIXED: Fetch doctor record ID for logged-in doctor
  const fetchDoctorRecord = async () => {
    try {
      const response = await axios.get('/api/doctors');
      const doctorRecord = response.data.find(d => d.user_id === user.id);
      if (doctorRecord) {
        setSelectedDoctor(doctorRecord.id.toString());
        // Now fetch payouts with the correct doctor ID
        fetchPayouts();
      }
    } catch (error) {
      console.error('Error fetching doctor record:', error);
      fetchPayouts(); // Still try to fetch payouts
    }
  };

  // Re-fetch payouts when selectedDoctor changes
  useEffect(() => {
    if (selectedDoctor || user.role === 'doctor') {
      fetchPayouts();
    }
  }, [selectedDoctor]);

  // Search effect - filter payouts based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      // If search term is empty, reset to all payouts
      setFilteredPayouts(allPayouts);
      setIsSearching(false);
    } else {
      // Filter payouts based on search term
      setIsSearching(true);
      const searchTermLower = searchTerm.toLowerCase();
      const formatted = formatPatientId(searchTerm); // Format search term as patient ID
      
      const filtered = allPayouts.filter(payout => {
        return (
          // Search by patient name
          (payout.patient_name && payout.patient_name.toLowerCase().includes(searchTermLower)) ||
          // Search by Patient ID (formatted)
          (payout.patient_id && formatPatientId(payout.patient_id).includes(searchTerm)) ||
          // Search by raw Patient ID
          (payout.patient_id && payout.patient_id.toString().includes(searchTerm)) ||
          // Search by doctor name (if user is admin/manager)
          (user.role !== 'doctor' && payout.doctor_name && payout.doctor_name.toLowerCase().includes(searchTermLower))
        );
      });
      
      setFilteredPayouts(filtered);
    }

    // Reset to first page when search term changes
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  }, [searchTerm, allPayouts]);

  // Pagination effect - update displayed payouts when page or filtered data changes
  useEffect(() => {
    updatePaginatedData();
  }, [pagination.currentPage, filteredPayouts]);

  const updatePaginatedData = () => {
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;

    // Get payouts for current page
    const paginatedPayouts = filteredPayouts.slice(startIndex, endIndex);
    setPayouts(paginatedPayouts);

    // Update pagination info
    const totalItems = filteredPayouts.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pagination.itemsPerPage));

    setPagination(prev => ({
      ...prev,
      totalPages,
      totalItems,
      currentPage: prev.currentPage > totalPages ? 1 : prev.currentPage
    }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({
        ...prev,
        currentPage: newPage
      }));

      // Scroll to top when page changes
      window.scrollTo({top: 0, behavior: 'smooth'});
    }
  };

  const fetchDoctors = async () => {
    try {
      const response = await axios.get('/api/doctors');
      setDoctors(response.data);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayouts = async () => {
    try {
      // FIXED: For doctors, the API will automatically filter by their doctor record ID
      // For other users, use the selected doctor filter
      const params = new URLSearchParams({
        ...dateRange,
        ...(selectedDoctor && user.role !== 'doctor' && {doctor_id: selectedDoctor})
      });

      const response = await axios.get(`/api/doctors/payouts?${params}`);
      setAllPayouts(response.data);
      setFilteredPayouts(response.data);
    } catch (error) {
      console.error('Error fetching payouts:', error);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setIsSearching(false);
    setFilteredPayouts(allPayouts);
  };

  // FIXED: Calculate summaries based on ALL payouts (not filtered)
  const getTotalPayouts = () => {
    return allPayouts.reduce((sum, payout) => sum + parseFloat(payout.payout_amount), 0);
  };

  const getPayoutsByDoctor = () => {
    // FIXED: Use allPayouts for doctor summary (not filtered)
    const grouped = allPayouts.reduce((acc, payout) => {
      const doctorName = payout.doctor_name;
      if (!acc[doctorName]) {
        acc[doctorName] = {
          total: 0,
          count: 0,
          payouts: []
        };
      }
      acc[doctorName].total += parseFloat(payout.payout_amount);
      acc[doctorName].count += 1;
      acc[doctorName].payouts.push(payout);
      return acc;
    }, {});

    return grouped;
  };

  // Helper function to get user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return "";
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  // Open view details modal - fetch invoice details
  const openViewModal = async (payout) => {
    try {
      // Find the invoice associated with this payout
      const response = await axios.get(`/api/invoices/${payout.invoice_id}`);
      setSelectedInvoice(response.data);
      setShowViewModal(true);
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      alert('Error loading invoice details');
    }
  };

  // Get status color for invoice status
  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'partial': return 'bg-yellow-100 text-yellow-800';
      case 'unpaid': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-grey-100 text-grey-800';
      default: return 'bg-grey-100 text-grey-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-maroon-600"></div>
      </div>
    );
  }

  const payoutsByDoctor = getPayoutsByDoctor();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-grey-900">
          {user.role === 'doctor' ? 'My Payouts' : 'Doctors & Payouts'}
        </h1>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Only show doctor selector if user is not a doctor */}
          {user.role !== 'doctor' && (
            <div>
              <label className="block text-sm font-medium text-grey-700 mb-1">
                Doctor
              </label>
              <select
                className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                value={selectedDoctor}
                onChange={(e) => setSelectedDoctor(e.target.value)}
              >
                <option value="">All Doctors</option>
                {doctors.map((doctor) => (
                  <option key={doctor.id} value={doctor.id}>
                    {doctor.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-grey-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
              value={dateRange.start_date}
              onChange={(e) => setDateRange({...dateRange, start_date: e.target.value})}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-grey-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
              value={dateRange.end_date}
              onChange={(e) => setDateRange({...dateRange, end_date: e.target.value})}
            />
          </div>
        </div>
      </div>

      {/* Summary Cards - FIXED: Always based on all payouts, not filtered */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Only show total doctors card if user is not a doctor */}
        {user.role !== 'doctor' && (
          <motion.div
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            className="bg-white shadow rounded-lg p-6"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiUserCheck} className="w-5 h-5 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-grey-600">Total Doctors</p>
                <p className="text-2xl font-semibold text-grey-900">{doctors.length}</p>
              </div>
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{opacity: 0, y: 20}}
          animate={{opacity: 1, y: 0}}
          transition={{delay: 0.1}}
          className="bg-white shadow rounded-lg p-6"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiDollarSign} className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-grey-600">
                {user.role === 'doctor' ? 'My Total Payouts' : 'Total Payouts'}
              </p>
              <p className="text-2xl font-semibold text-grey-900">QR {getTotalPayouts().toLocaleString()}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{opacity: 0, y: 20}}
          animate={{opacity: 1, y: 0}}
          transition={{delay: 0.2}}
          className="bg-white shadow rounded-lg p-6"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-grey-600">Total Transactions</p>
              <p className="text-2xl font-semibold text-grey-900">{allPayouts.length}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Doctor Summary - FIXED: Only show if user is not a doctor and no specific doctor is selected, always based on all payouts */}
      {!selectedDoctor && user.role !== 'doctor' && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-grey-200">
            <h3 className="text-lg font-medium text-grey-900">Doctor Summary</h3>
          </div>
          <div className="divide-y divide-grey-200">
            {Object.entries(payoutsByDoctor).map(([doctorName, data]) => {
              // Find the doctor data to get profile image
              const doctorData = doctors.find(d => d.name === doctorName);
              return (
                <motion.div
                  key={doctorName}
                  initial={{opacity: 0}}
                  animate={{opacity: 1}}
                  className="p-6"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {doctorData?.profile_image ? (
                        <div className="h-10 w-10 rounded-full overflow-hidden mr-3">
                          <img
                            src={doctorData.profile_image}
                            alt={doctorName}
                            className="h-full w-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                          <span className="text-green-800 font-medium text-sm">
                            {getUserInitials(doctorName)}
                          </span>
                        </div>
                      )}
                      <div>
                        <h4 className="text-lg font-medium text-grey-900">{doctorName}</h4>
                        <p className="text-sm text-grey-600">{data.count} transactions</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-semibold text-grey-900">
                        QR {data.total.toLocaleString()}
                      </p>
                      <p className="text-sm text-grey-600">
                        Avg: QR {(data.total / data.count).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* Detailed Payouts */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-grey-200">
          <h3 className="text-lg font-medium text-grey-900">
            {user.role === 'doctor' ? 'My Payout Details' : 'Payout Details'}
          </h3>
        </div>
        
        {/* Sticky Search Bar Section */}
        <div className="sticky top-0 z-10 bg-white border-b border-grey-200 shadow-sm">
          <div className="px-6 py-4 bg-grey-50">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-grey-400 w-5 h-5" />
              <input
                type="text"
                placeholder={user.role === 'doctor' 
                  ? "Search by patient name or patient ID..." 
                  : "Search by patient name, patient ID, or doctor name..."
                }
                className="w-full pl-10 pr-10 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-grey-400 hover:text-grey-600"
                  onClick={clearSearch}
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              )}
            </div>

            <div className="mt-2 text-sm text-grey-600">
              {pagination.totalItems} payout record{pagination.totalItems !== 1 ? 's' : ''} found
              {isSearching && ` (filtered from ${allPayouts.length} total)`}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-grey-200">
            <thead className="bg-grey-50">
              <tr>
				<th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                  Patient ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                  Date
                </th>
                {user.role !== 'doctor' && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                    Doctor
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                  Patient
                </th>
                
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                  Gross Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                  Deduction
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                  Payout Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                  Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-grey-200">
              {payouts.length === 0 ? (
                <tr>
                  <td colSpan={user.role === 'doctor' ? 8 : 9} className="px-6 py-4 text-center text-grey-500">
                    {isSearching 
                      ? "No payout records match your search criteria"
                      : "No payout records found for the selected criteria"
                    }
                  </td>
                </tr>
              ) : (
                payouts.map((payout) => (
                  <motion.tr
                    key={payout.id}
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    className="hover:bg-grey-50"
                  >
					<td className="px-6 py-4 whitespace-nowrap text-sm text-grey-900">
                      {payout.patient_id ? `#${formatPatientId(payout.patient_id)}` : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-grey-900">
                      {format(parseISO(payout.created_at), 'MMM dd, yyyy')}
                    </td>
                    {user.role !== 'doctor' && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {/* Find the doctor to get their profile image */}
                          {(() => {
                            const doctorData = doctors.find(d => d.name === payout.doctor_name);
                            return doctorData?.profile_image ? (
                              <div className="h-8 w-8 rounded-full overflow-hidden mr-2">
                                <img
                                  src={doctorData.profile_image}
                                  alt={payout.doctor_name}
                                  className="h-full w-full object-cover"
                                />
                              </div>
                            ) : (
                              <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center mr-2">
                                <span className="text-green-800 font-medium text-xs">
                                  {getUserInitials(payout.doctor_name)}
                                </span>
                              </div>
                            );
                          })()}
                          <span className="text-sm font-medium text-grey-900">{payout.doctor_name}</span>
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-grey-900">
                      {payout.patient_name}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-grey-900">
                      QR {parseFloat(payout.gross_amount).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                      QR {parseFloat(payout.deduction).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      QR {parseFloat(payout.payout_amount).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-grey-900 capitalize">
                      {payout.payment_method}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => openViewModal(payout)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View details"
                      >
                        <SafeIcon icon={FiEye} className="w-4 h-4" />
                      </button>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 bg-grey-50 border-t border-grey-200 flex flex-wrap items-center justify-between">
            <div className="text-sm text-grey-700">
              Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
              {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
              {pagination.totalItems} payout records
            </div>
            <div className="flex space-x-2 mt-2 sm:mt-0">
              <button
                onClick={() => handlePageChange(1)}
                disabled={pagination.currentPage === 1}
                className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="First Page"
              >
                <SafeIcon icon={FiChevronsLeft} className="w-5 h-5" />
              </button>
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous Page"
              >
                <SafeIcon icon={FiChevronLeft} className="w-5 h-5" />
              </button>
              <div className="flex items-center px-4">
                <span className="text-grey-700">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
              </div>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next Page"
              >
                <SafeIcon icon={FiChevronRight} className="w-5 h-5" />
              </button>
              <button
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Last Page"
              >
                <SafeIcon icon={FiChevronsRight} className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View Invoice Modal - Same as in Invoices page */}
      {showViewModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{scale: 0.95, opacity: 0}}
            animate={{scale: 1, opacity: 1}}
            className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-screen overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-grey-900">
                Invoice #{selectedInvoice.id}
              </h3>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedInvoice.status)}`}>
                {selectedInvoice.status}
              </span>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Patient
                  </label>
                  <p className="text-sm text-grey-900">{selectedInvoice.patient_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Doctor
                  </label>
                  <p className="text-sm text-grey-900">{selectedInvoice.doctor_name || 'N/A'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Date
                  </label>
                  <p className="text-sm text-grey-900">
                    {format(parseISO(selectedInvoice.created_at), 'MMM dd, yyyy')}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Payment Method
                  </label>
                  <p className="text-sm text-grey-900 capitalize">{selectedInvoice.payment_method}</p>
                </div>
              </div>

              {selectedInvoice.treatments && selectedInvoice.treatments.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-2">
                    Treatments
                  </label>
                  <div className="space-y-2">
                    {selectedInvoice.treatments.map((treatment, index) => (
                      <div key={index} className="bg-grey-50 p-3 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium text-grey-900">{treatment.description}</p>
                            {treatment.treatment_code && (
                              <p className="text-xs text-grey-500">Code: {treatment.treatment_code}</p>
                            )}
                            {treatment.is_specialist && (
                              <p className="text-xs text-blue-600">Specialist Treatment</p>
                            )}
                          </div>
                          <p className="text-sm font-medium text-grey-900">QR {treatment.cost}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-blue-700">Total Amount</p>
                    <p className="font-medium text-blue-900">QR {selectedInvoice.total_amount}</p>
                  </div>
                  <div>
                    <p className="text-blue-700">Paid Amount</p>
                    <p className="font-medium text-blue-900">QR {selectedInvoice.paid_amount}</p>
                  </div>
                  <div>
                    <p className="text-blue-700">Outstanding</p>
                    <p className="font-medium text-blue-900">
                      QR {selectedInvoice.total_amount - selectedInvoice.paid_amount}
                    </p>
                  </div>
                </div>
              </div>

              {selectedInvoice.payments && selectedInvoice.payments.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-2">
                    Payment History
                  </label>
                  <div className="space-y-2">
                    {selectedInvoice.payments.map((payment, index) => (
                      <div key={index} className="bg-green-50 p-3 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium text-green-900">
                              QR {payment.amount} - {payment.payment_method}
                            </p>
                            <p className="text-xs text-green-700">
                              {format(parseISO(payment.payment_date), 'MMM dd, yyyy HH:mm')}
                            </p>
                            <p className="text-xs text-green-600">By: {payment.recorded_by_name}</p>
                            {payment.notes && (
                              <p className="text-xs text-green-600 mt-1">{payment.notes}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedInvoice.manual_deductions && selectedInvoice.manual_deductions.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-2">
                    Manual Deductions
                  </label>
                  <div className="space-y-2">
                    {selectedInvoice.manual_deductions.map((deduction, index) => (
                      <div key={index} className="bg-orange-50 p-3 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium text-orange-900">
                              QR {deduction.amount} deduction
                            </p>
                            <p className="text-xs text-orange-700">
                              Reason: {deduction.reason}
                            </p>
                            <p className="text-xs text-orange-600">
                              {format(parseISO(deduction.created_at), 'MMM dd, yyyy HH:mm')}
                            </p>
                            <p className="text-xs text-orange-600">By: {deduction.created_by_name}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedInvoice.doctor_payout && (
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-purple-800 mb-2">Doctor Payout Information</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-purple-700">Gross Amount</p>
                      <p className="font-medium text-purple-900">QR {selectedInvoice.doctor_payout.gross_amount}</p>
                    </div>
                    <div>
                      <p className="text-purple-700">Total Deductions</p>
                      <p className="font-medium text-purple-900">QR {selectedInvoice.doctor_payout.deduction}</p>
                    </div>
                    <div>
                      <p className="text-purple-700">Final Payout</p>
                      <p className="font-medium text-purple-900">QR {selectedInvoice.doctor_payout.payout_amount}</p>
                    </div>
                  </div>
                  {selectedInvoice.doctor_payout.manual_deduction_reason && (
                    <div className="mt-2">
                      <p className="text-xs text-purple-600">
                        Manual deduction applied: {selectedInvoice.doctor_payout.manual_deduction_reason}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {selectedInvoice.status === 'cancelled' && (
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-red-800">Cancellation Reason:</p>
                  <p className="text-sm text-red-700">{selectedInvoice.cancellation_reason}</p>
                  {selectedInvoice.cancelled_by_name && (
                    <p className="text-xs text-red-600 mt-1">
                      Cancelled by: {selectedInvoice.cancelled_by_name}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 bg-grey-600 text-white rounded-lg hover:bg-grey-700 transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Doctors;